import { useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { mono, sans } from '../../ui/atoms.js';
import type { RoundCopy } from '../../ui/roundText.js';

/**
 * The saboteur's game, second version (games/Hack.tsx): trace the circuit. Six numbered dots lie
 * scattered on a board; tap them from 1 to 6 and a line joins them as it goes. A wrong dot shakes
 * and the line starts again from 1 — nothing worse. Then the four-digit code, as in the first.
 */

/** How many dots, how far apart two sit, and how far in from the edge (shares of the board's side). */
export const TRACE_DOTS = 6;
const APART = 0.26;
const EDGE = 0.12;
/** A line from one dot to the next keeps this far from every other dot, so it never seems to join one. */
const CLEAR = 0.09;

export interface Dot {
  x: number;
  y: number;
}

const apart = (a: Dot, b: Dot) => Math.hypot(a.x - b.x, a.y - b.y);

/** How far a dot is from the line between two others. */
function offLine(p: Dot, a: Dot, b: Dot): number {
  const dx = b.x - a.x;
  const dy = b.y - a.y;
  const t = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / (dx * dx + dy * dy || 1)));
  return Math.hypot(p.x - (a.x + t * dx), p.y - (a.y + t * dy));
}

/** Whether a board reads at a glance: every dot inside, apart from the others, and clear of the lines. */
export function traceReads(dots: readonly Dot[]): boolean {
  if (dots.length !== TRACE_DOTS) return false;
  if (dots.some((d) => d.x < EDGE || d.x > 1 - EDGE || d.y < EDGE || d.y > 1 - EDGE)) return false;
  for (let i = 0; i < dots.length; i++) {
    for (let j = i + 1; j < dots.length; j++) if (apart(dots[i]!, dots[j]!) < APART) return false;
  }
  for (let i = 0; i + 1 < dots.length; i++) {
    for (let k = 0; k < dots.length; k++) {
      if (k !== i && k !== i + 1 && offLine(dots[k]!, dots[i]!, dots[i + 1]!) < CLEAR) return false;
    }
  }
  return true;
}

/** A fresh board every game: dot i is number i + 1, as fractions of the board's side. */
export function layTrace(random: () => number = Math.random): Dot[] {
  const span = 1 - 2 * EDGE;
  for (let tries = 0; tries < 400; tries++) {
    const dots: Dot[] = [];
    for (let guard = 0; dots.length < TRACE_DOTS && guard < 200; guard++) {
      const d = { x: EDGE + random() * span, y: EDGE + random() * span };
      if (dots.every((o) => apart(d, o) >= APART)) dots.push(d);
    }
    if (traceReads(dots)) return dots;
  }
  // Hardly ever needed: a ring, turned a fresh way, still plays.
  const turn = random() * Math.PI * 2;
  return Array.from({ length: TRACE_DOTS }, (_, i) => {
    const a = turn + (i * Math.PI * 2) / TRACE_DOTS;
    return { x: 0.5 + 0.34 * Math.cos(a), y: 0.5 + 0.34 * Math.sin(a) };
  });
}

/** A shake to the side and back: movement only, no change of light. */
const SHAKE: Keyframe[] = [
  { transform: 'translateX(0)' },
  { transform: 'translateX(-7px)' },
  { transform: 'translateX(6px)' },
  { transform: 'translateX(-4px)' },
  { transform: 'translateX(0)' },
];

export function TraceBoard({
  r,
  onDone,
  onStep,
  onMistake,
}: {
  r: RoundCopy;
  onDone: () => void;
  /** Each dot joined: how many are on the line now. */
  onStep?: (joined: number) => void;
  /** A dot out of order. */
  onMistake?: () => void;
}) {
  const [dots] = useState(() => layTrace());
  const [path, setPath] = useState<number[]>([]);
  const [wrong, setWrong] = useState(false);
  const pads = useRef<(HTMLButtonElement | null)[]>([]);

  const tap = (dot: number) => {
    // A dot already on the line is no mistake; it just does nothing.
    if (path.includes(dot)) return;
    if (dot !== path.length) {
      pads.current[dot]?.animate?.(SHAKE, { duration: 320, easing: 'ease-out' });
      navigator.vibrate?.(30);
      setPath([]);
      setWrong(true);
      onMistake?.();
      return;
    }
    const next = [...path, dot];
    setPath(next);
    setWrong(false);
    onStep?.(next.length);
    if (next.length === TRACE_DOTS) onDone();
  };

  return (
    <>
      <div style={{ fontFamily: sans, fontSize: 20, fontWeight: 600, lineHeight: 1.3 }}>{r.hackTrace}</div>
      <div
        data-nodrag
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: 320,
          aspectRatio: '1 / 1',
          boxSizing: 'border-box',
          margin: '16px auto 0',
          borderRadius: 16,
          border: '1px solid rgba(255,95,134,.3)',
          // A circuit board: a faint grid, drawn once and never moving.
          backgroundColor: '#070a0f',
          backgroundImage:
            'linear-gradient(rgba(255,255,255,.05) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.05) 1px, transparent 1px)',
          backgroundSize: '10% 10%',
        }}
      >
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" style={{ position: 'absolute', inset: 0, width: '100%', height: '100%' }}>
          <polyline
            points={path.map((i) => `${dots[i]!.x * 100},${dots[i]!.y * 100}`).join(' ')}
            fill="none"
            stroke="#ff5f86"
            strokeWidth={4}
            strokeLinecap="round"
            strokeLinejoin="round"
            vectorEffect="non-scaling-stroke"
          />
        </svg>
        {dots.map((d, i) => (
          <button
            key={i}
            ref={(el) => {
              pads.current[i] = el;
            }}
            onClick={() => tap(i)}
            style={pad(d, path.includes(i))}
          >
            {i + 1}
          </button>
        ))}
      </div>
      <div style={{ minHeight: 26, marginTop: 14, fontFamily: sans, fontSize: 18, fontWeight: 600, color: '#ffd1da' }}>{wrong ? r.hackTraceWrong : ''}</div>
    </>
  );
}

/** A dot: a round pad over the line, filled once the line has reached it. */
function pad(d: Dot, joined: boolean): CSSProperties {
  return {
    position: 'absolute',
    left: `calc(${d.x * 100}% - 26px)`,
    top: `calc(${d.y * 100}% - 26px)`,
    width: 52,
    height: 52,
    padding: 0,
    borderRadius: 26,
    border: `3px solid ${joined ? '#ff5f86' : 'rgba(255,255,255,.55)'}`,
    background: joined ? '#ff5f86' : '#0b0f15',
    color: joined ? '#2a0610' : '#ffffff',
    fontFamily: mono,
    fontSize: 22,
    fontWeight: 700,
    touchAction: 'manipulation',
    transition: 'background-color .2s ease',
  };
}
